import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import practitionerService from '../services/PractitionerService';
import appointmentService from '../services/AppointmentService';
import treatmentRecordService from '../services/TreatmentRecordService';
import { Card, Button, Input, Select, Modal, Alert } from '../components/ui';

/**
 * PractitionerDetailPage component for viewing and managing a specific practitioner
 * @returns {React.ReactElement} PractitionerDetailPage component
 */
const PractitionerDetailPage = () => {
  // Get practitioner id from URL params
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  // State
  const [practitioner, setPractitioner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    gender: '',
    age: '',
    title: '',
    education: '',
    certifications: '',
    experienceYears: '',
    workHistory: '',
    honors: '',
    avatarUrl: '',
    phoneNumber: '',
    email: '',
    specialties: '',
    bio: '',
    languages: '',
    consultationFee: '',
    tags: '',
    wechat: '',
    address: '',
    scheduleNote: '',
    isExpert: false,
    sortOrder: 0,
    isActive: true
  });
  const [formErrors, setFormErrors] = useState({});

  // Activity stats
  const [stats, setStats] = useState({
    upcomingAppointments: 0,
    completedAppointments: 0,
    totalTreatmentRecords: 0
  });

  // Check permissions
  const canViewPractitioner = hasPermission('practitioners', 'read');
  const canEditPractitioner = hasPermission('practitioners', 'write');

  // Copy success state
  const [copySuccess, setCopySuccess] = useState(false);
  const emailRef = useRef(null);

  // Avatar error state
  const [avatarError, setAvatarError] = useState(false);

  // Fetch practitioner data
  useEffect(() => {
    if (!id || !canViewPractitioner) {
      return;
    }

    const fetchPractitioner = () => {
      setLoading(true);
      try {
        const practitionerData = practitionerService.getPractitionerDetails(id);
        if (!practitionerData) {
          setError('找不到调理师');
          return;
        }
        setPractitioner(practitionerData);
        setFormData({
          name: practitionerData.name || '',
          gender: practitionerData.gender || '',
          age: practitionerData.age || '',
          title: practitionerData.title || '',
          education: practitionerData.education || '',
          certifications: Array.isArray(practitionerData.certifications) ? practitionerData.certifications.join(', ') : practitionerData.certifications || '',
          experienceYears: practitionerData.experienceYears || '',
          workHistory: Array.isArray(practitionerData.workHistory) ? practitionerData.workHistory.map(w => `${w.organization} (${w.title}, ${w.from}-${w.to})`).join('; ') : '',
          honors: Array.isArray(practitionerData.honors) ? practitionerData.honors.join(', ') : practitionerData.honors || '',
          avatarUrl: practitionerData.avatarUrl || '',
          phoneNumber: practitionerData.phoneNumber || practitionerData.phone || '',
          email: practitionerData.email || '',
          specialties: Array.isArray(practitionerData.specialties) ? practitionerData.specialties.join(', ') : practitionerData.specialties || '',
          bio: practitionerData.bio || practitionerData.introduction || '',
          languages: Array.isArray(practitionerData.languages) ? practitionerData.languages.join(', ') : practitionerData.languages || '',
          consultationFee: practitionerData.consultationFee || '',
          tags: Array.isArray(practitionerData.tags) ? practitionerData.tags.join(', ') : practitionerData.tags || '',
          wechat: practitionerData.wechat || '',
          address: practitionerData.address || '',
          scheduleNote: practitionerData.scheduleNote || '',
          isExpert: practitionerData.isExpert || false,
          sortOrder: practitionerData.sortOrder || 0,
          isActive: practitionerData.isActive !== false
        });

        // Fetch stats
        const allAppointments = appointmentService.getAllAppointments()
          .filter(appt => appt.practitionerId === id);

        const upcomingAppointments = allAppointments.filter(appt =>
          (appt.status === 'scheduled' || appt.status === 'confirmed') &&
          new Date(appt.appointmentTime) > new Date()
        );

        const completedAppointments = allAppointments.filter(appt =>
          appt.status === 'completed'
        );

        const treatmentRecords = treatmentRecordService.getAllTreatmentRecords()
          .filter(record => record.practitionerId === id);

        setStats({
          upcomingAppointments: upcomingAppointments.length,
          completedAppointments: completedAppointments.length,
          totalTreatmentRecords: treatmentRecords.length
        });

        setError(null);
      } catch (err) {
        console.error('Error fetching practitioner:', err);
        setError('获取调理师数据失败');
      } finally {
        setLoading(false);
      }
    };

    fetchPractitioner();
  }, [id, canViewPractitioner]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = '请输入调理师姓名';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = '请输入有效的邮箱地址';
    }

    if (formData.phoneNumber && !/^1[3-9]\d{9}$/.test(formData.phoneNumber.replace(/\s|-/g, ''))) {
      errors.phoneNumber = '请输入有效的手机号码';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      // Prepare data for submission
      const submitData = {
        ...formData,
        specialties: formData.specialties ? formData.specialties.split(',').map(s => s.trim()).filter(s => s) : [],
        certifications: formData.certifications ? formData.certifications.split(',').map(s => s.trim()).filter(s => s) : [],
        honors: formData.honors ? formData.honors.split(',').map(s => s.trim()).filter(s => s) : [],
        languages: formData.languages ? formData.languages.split(',').map(s => s.trim()).filter(s => s) : [],
        tags: formData.tags ? formData.tags.split(',').map(s => s.trim()).filter(s => s) : [],
        workHistory: formData.workHistory ? formData.workHistory.split(';').map(s => {
          const match = s.match(/(.+?) \((.+?), (.+?)-(.*?)\)/);
          return match ? { organization: match[1].trim(), title: match[2].trim(), from: match[3].trim(), to: match[4].trim() } : null;
        }).filter(Boolean) : [],
        phoneNumber: formData.phoneNumber,
        phone: formData.phoneNumber
      };

      practitionerService.updatePractitioner(id, submitData);

      // Refresh practitioner data
      const refreshedPractitioner = practitionerService.getPractitionerById(id);
      setPractitioner(refreshedPractitioner);

      // Exit edit mode
      setIsEditing(false);
      setFormErrors({});
    } catch (err) {
      console.error('Error updating practitioner:', err);
      setFormErrors({ submit: '更新调理师信息失败，请重试' });
    }
  };

  // Handle delete
  const handleDelete = () => {
    if (window.confirm('确定要删除此调理师吗？此操作无法撤销，且可能影响关联的预约和调理记录。')) {
      try {
        practitionerService.deletePractitioner(id);
        navigate('/practitioners');
      } catch (err) {
        console.error('Error deleting practitioner:', err);
        alert('删除调理师失败');
      }
    }
  };

  // If there's an error finding the practitioner
  if (error === '找不到调理师') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-emerald-50/30 to-blue-50/30 flex items-center justify-center p-6">
        <Card variant="simple" className="max-w-md w-full text-center">
          <div className="w-16 h-16 mx-auto bg-red-100 rounded-2xl flex items-center justify-center mb-6">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">调理师未找到</h2>
          <p className="text-gray-600 mb-6">您尝试访问的调理师不存在或已被删除</p>
          <Button onClick={() => navigate('/practitioners')} className="w-full">
            返回调理师列表
          </Button>
        </Card>
      </div>
    );
  }

  // If user doesn't have permission
  if (!canViewPractitioner) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-emerald-50/30 to-blue-50/30 flex items-center justify-center p-6">
        <Card variant="simple" className="max-w-md w-full text-center">
          <div className="w-16 h-16 mx-auto bg-red-100 rounded-2xl flex items-center justify-center mb-6">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">权限不足</h2>
          <p className="text-gray-600 mb-6">您没有查看调理师详情的权限</p>
          <Button onClick={() => navigate('/')} className="w-full">
            返回首页
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-emerald-50/30 to-blue-50/30 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <Card variant="glass" className="border-emerald-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/practitioners')}
                className="shrink-0"
              >
                <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                返回调理师列表
              </Button>
              <div className="border-l border-gray-200 pl-4">
                {loading ? (
                  <div className="animate-pulse">
                    <div className="h-8 bg-gray-200 rounded w-48 mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-32"></div>
                  </div>
                ) : practitioner ? (
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900">
                      {practitioner.name}
                    </h1>
                    <p className="text-gray-600">
                      调理师详情及活动统计
                    </p>
                  </div>
                ) : (
                  <h1 className="text-2xl font-bold text-gray-900">
                    调理师详情
                  </h1>
                )}
              </div>
            </div>
            {practitioner && canEditPractitioner && (
              <Button
                onClick={() => setIsEditing(true)}
                className="shrink-0"
              >
                <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                编辑信息
              </Button>
            )}
          </div>
        </Card>
        {/* 错误提示 */}
        {error && error !== '找不到调理师' && (
          <Alert type="error" onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}
        {/* 加载中 */}
        {loading ? (
          <Card variant="modern">
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-2 border-emerald-500 border-t-transparent"></div>
            </div>
          </Card>
        ) : practitioner ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Practitioner Info */}
            <div className="lg:col-span-1">
              <Card variant="modern" className="border-emerald-100 h-fit min-w-[420px] md:max-w-2xl">
                <div className="flex items-center space-x-3 mb-6">
                  {practitioner.avatarUrl && !avatarError ? (
                    <img
                      src={practitioner.avatarUrl}
                      alt="头像"
                      className="w-12 h-12 rounded-xl object-cover shadow-lg"
                      onError={() => setAvatarError(true)}
                    />
                  ) : (
                    <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-lg">
                      {practitioner.name ? practitioner.name.charAt(0) : '?'}
                    </div>
                  )}
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">{practitioner.name}</h3>
                    <p className="text-gray-600 text-sm">{practitioner.title || '未设置职称'}</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 mb-4">
                  <div className="flex items-center space-x-2 py-1"><span className="text-sm text-gray-600 font-medium">性别：</span><span className="text-sm text-gray-900">{practitioner.gender || '未提供'}</span></div>
                  <div className="flex items-center space-x-2 py-1"><span className="text-sm text-gray-600 font-medium">年龄：</span><span className="text-sm text-gray-900">{practitioner.age ? `${practitioner.age} 岁` : '未提供'}</span></div>
                  <div className="flex items-center space-x-2 py-1"><span className="text-sm text-gray-600 font-medium">学历：</span><span className="text-sm text-gray-900">{practitioner.education || '未提供'}</span></div>
                  <div className="flex items-center space-x-2 py-1"><span className="text-sm text-gray-600 font-medium">联系电话：</span><span className="text-sm text-gray-900 break-keep overflow-auto">{practitioner.phoneNumber || '未提供'}</span></div>
                  <div className="flex items-center space-x-2 py-1 whitespace-nowrap">
                    <span className="text-sm text-gray-600 font-medium">邮箱：</span>
                    <span
                      className="text-sm text-gray-900 truncate max-w-[160px] inline-block align-middle"
                      title={practitioner.email || '未提供'}
                      ref={emailRef}
                    >
                      {practitioner.email || '未提供'}
                    </span>
                    {practitioner.email && (
                      <Button
                        size="small"
                        variant="secondary"
                        className="ml-1 px-1 py-0.5 text-xs flex items-center justify-center flex-shrink-0"
                        onClick={() => {
                          if (emailRef.current) {
                            navigator.clipboard.writeText(practitioner.email);
                            setCopySuccess(true);
                            setTimeout(() => setCopySuccess(false), 1200);
                          }
                        }}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><rect x="9" y="9" width="13" height="13" rx="2" stroke="currentColor" strokeWidth="2" fill="none" /><path stroke="currentColor" strokeWidth="2" d="M5 15V5a2 2 0 012-2h10a2 2 0 012 2v10" /></svg>
                      </Button>
                    )}
                    {copySuccess && <span className="ml-2 text-emerald-600 text-xs">已复制</span>}
                  </div>
                  <div className="flex items-center space-x-2 py-1"><span className="text-sm text-gray-600 font-medium">微信：</span><span className="text-sm text-gray-900">{practitioner.wechat || '未提供'}</span></div>
                  <div className="flex items-center space-x-2 py-1"><span className="text-sm text-gray-600 font-medium">费用：</span><span className="text-sm text-gray-900">{practitioner.consultationFee || '未提供'}</span></div>
                  <div className="flex items-center space-x-2 py-1"><span className="text-sm text-gray-600 font-medium">地址：</span><span className="text-sm text-gray-900 break-keep overflow-auto">{practitioner.address || '未提供'}</span></div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 mt-4 pt-4 border-t border-gray-100">
                  <div>
                    <div className="text-sm text-gray-600 font-medium mb-1">专长</div>
                    <div className="text-sm text-gray-900 bg-gray-50 rounded p-2 border border-gray-200 min-h-[32px]">
                      {Array.isArray(practitioner.specialties) ? practitioner.specialties.join('，') : (practitioner.specialties || '未设置')}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 font-medium mb-1">标签</div>
                    <div className="text-sm text-gray-900 bg-gray-50 rounded p-2 border border-gray-200 min-h-[32px]">
                      {Array.isArray(practitioner.tags) ? practitioner.tags.join('，') : (practitioner.tags || '未设置')}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 font-medium mb-1">荣誉</div>
                    <div className="text-sm text-gray-900 bg-gray-50 rounded p-2 border border-gray-200 min-h-[32px]">
                      {Array.isArray(practitioner.honors) ? practitioner.honors.join('，') : (practitioner.honors || '未设置')}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 font-medium mb-1">证书</div>
                    <div className="text-sm text-gray-900 bg-gray-50 rounded p-2 border border-gray-200 min-h-[32px]">
                      {Array.isArray(practitioner.certifications) ? practitioner.certifications.join('，') : (practitioner.certifications || '未设置')}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 font-medium mb-1">语言</div>
                    <div className="text-sm text-gray-900 bg-gray-50 rounded p-2 border border-gray-200 min-h-[32px]">
                      {Array.isArray(practitioner.languages) ? practitioner.languages.join('，') : (practitioner.languages || '未设置')}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 font-medium mb-1">简介</div>
                    <div className="text-sm text-gray-900 bg-gray-50 rounded p-2 border border-gray-200 min-h-[32px]">
                      {practitioner.bio || '未填写'}
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <div className="text-sm text-gray-600 font-medium mb-1">工作经历</div>
                    <div className="text-sm text-gray-900 bg-gray-50 rounded p-2 border border-gray-200 min-h-[32px]">
                      {Array.isArray(practitioner.workHistory) && practitioner.workHistory.length > 0
                        ? practitioner.workHistory.map((w, idx) => `${w.organization}（${w.title}，${w.from}-${w.to}）`).join('；')
                        : '未填写'}
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <div className="text-sm text-gray-600 font-medium mb-1">排班备注</div>
                    <div className="text-sm text-gray-900 bg-gray-50 rounded p-2 border border-gray-200 min-h-[32px]">
                      {practitioner.scheduleNote || '未填写'}
                    </div>
                  </div>
                </div>
              </Card>
            </div>
            {/* 活动统计和操作 */}
            <div className="lg:col-span-2 space-y-6">
              <Card variant="modern">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">活动统计</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="flex items-center justify-between p-4 bg-blue-50 rounded-xl border border-blue-200">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm text-blue-600">即将到来的预约</p>
                        <p className="text-2xl font-bold text-blue-700">{stats.upcomingAppointments}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-green-50 rounded-xl border border-green-200">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                        <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm text-green-600">已完成的预约</p>
                        <p className="text-2xl font-bold text-green-700">{stats.completedAppointments}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-purple-50 rounded-xl border border-purple-200">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                        <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm text-purple-600">调理记录总数</p>
                        <p className="text-2xl font-bold text-purple-700">{stats.totalTreatmentRecords}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
              {/* 快速操作 */}
              <Card variant="modern">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">快速操作</h4>
                <div className="flex flex-wrap gap-3">
                  <Button
                    onClick={() => navigate(`/appointments?practitionerId=${id}`)}
                    variant="secondary"
                  >
                    查看预约记录
                  </Button>
                  <Button
                    onClick={() => navigate(`/treatment-records?practitionerId=${id}`)}
                    variant="secondary"
                  >
                    查看调理记录
                  </Button>
                  <Button
                    onClick={handleDelete}
                    variant="danger"
                  >
                    删除调理师
                  </Button>
                </div>
              </Card>
            </div>
          </div>
        ) : null}
      </div>
      {/* 编辑弹窗 */}
      <Modal
        isOpen={isEditing}
        onClose={() => {
          setIsEditing(false);
          setFormErrors({});
        }}
        title="编辑调理师信息"
        size="large"
        footer={
          <div className="flex space-x-3">
            <Button
              variant="secondary"
              onClick={() => {
                setIsEditing(false);
                setFormErrors({});
              }}
            >
              取消
            </Button>
            <Button
              type="submit"
              form="edit-practitioner-form"
            >
              保存修改
            </Button>
          </div>
        }
      >
        <form id="edit-practitioner-form" onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input label="姓名" name="name" value={formData.name} onChange={handleInputChange} error={formErrors.name} required placeholder="请输入调理师姓名" />
            <Input label="性别" name="gender" value={formData.gender} onChange={handleInputChange} placeholder="请输入性别，如男/女" />
            <Input label="年龄" name="age" type="number" min="0" value={formData.age} onChange={handleInputChange} placeholder="请输入年龄" />
            <Input label="职称" name="title" value={formData.title} onChange={handleInputChange} placeholder="如主任医师/教授等" />
            <Input label="学历" name="education" value={formData.education} onChange={handleInputChange} placeholder="如博士/硕士/本科等" />
            <Input label="联系电话" name="phoneNumber" value={formData.phoneNumber} onChange={handleInputChange} error={formErrors.phoneNumber} placeholder="请输入手机号码" />
            <Input label="邮箱" name="email" value={formData.email} onChange={handleInputChange} error={formErrors.email} placeholder="请输入邮箱地址" />
            <Input label="微信" name="wechat" value={formData.wechat} onChange={handleInputChange} placeholder="请输入微信号" />
            <Input label="费用" name="consultationFee" value={formData.consultationFee} onChange={handleInputChange} placeholder="如500元/次" />
            <Input label="地址" name="address" value={formData.address} onChange={handleInputChange} placeholder="请输入工作地址" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input label="专长（逗号分隔）" name="specialties" value={formData.specialties} onChange={handleInputChange} placeholder="如针灸,推拿,调理等" />
            <Input label="标签（逗号分隔）" name="tags" value={formData.tags} onChange={handleInputChange} placeholder="如青少年增高,亚健康等" />
            <Input label="荣誉（逗号分隔）" name="honors" value={formData.honors} onChange={handleInputChange} placeholder="如国医大师,项目创始人等" />
            <Input label="证书（逗号分隔）" name="certifications" value={formData.certifications} onChange={handleInputChange} placeholder="如执业医师证,针灸证书等" />
            <Input label="语言（逗号分隔）" name="languages" value={formData.languages} onChange={handleInputChange} placeholder="如中文,英文等" />
            <Input label="简介" name="bio" value={formData.bio} onChange={handleInputChange} placeholder="请输入调理师简介" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">工作经历（格式：单位（职位，起止时间）；多个用分号分隔）</label>
            <textarea name="workHistory" rows={2} value={formData.workHistory} onChange={handleInputChange} className="w-full px-4 py-3 text-sm border border-gray-300 bg-white text-gray-900 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200" placeholder="如：中医院（主任医师，2010-2020）；国保中心（院长，2020-至今）" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">排班备注</label>
            <textarea name="scheduleNote" rows={2} value={formData.scheduleNote} onChange={handleInputChange} className="w-full px-4 py-3 text-sm border border-gray-300 bg-white text-gray-900 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200" placeholder="如：需提前预约，周一三五坐诊等" />
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default PractitionerDetailPage;